/* eslint-disable */
import { Image, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Path, Rect, Stop } from 'react-native-svg';
import { useFocusEffect } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toast from 'react-native-toast-message';
import AsyncStorage from '@react-native-async-storage/async-storage';
import AuthHeader from '../../components/AuthHeader';
import { LanguageModal } from '../../components/LanguageModal';
import LogoutConfirm from '../../components/LogoutConfirm';
import DeleteAccountModal from '../../components/DeleteAccountModal';
import FuelTypeModal, { FUEL_TYPES } from '../../components/FuelTypeModal';
import AppIcon from '../../components/onboarding/AppIcon';
import InAppWebView from '../../components/InAppWebView';
import { WEB_PAGES } from '../../config/webPages';
import EnglishFlag from '../../assets/English.svg';
import DutchFlag from '../../assets/Dutch.svg';
import { logoutUser } from '../../slice/loginSlice';
import { getProfile } from '../../slice/profileSlice';
import {
  getSavedOnboardingAnswers,
  updateFuelType,
} from '../../services/onboarding-service';
import { getFavouritesApi } from '../../services/auth-service';
import { getRewards } from '../../services/rewards-service';
import { deleteAccount } from '../../services/account-service';
import { setDevUnlocked, useDevSettings } from '../../config/devSettings';
import { colors, card, fonts, radii, spacing, typography } from '../../config/theme';

const APP_VERSION = '1.0.0';
const AVATAR_SIZE = 64;
const ICON_TILE = 36;
// The profile is not downloaded again if it was loaded less than this long ago.
const PROFILE_MAX_AGE_MS = 60000;

// Per-row icon tints from Stitch "Pompi - Profile Screen".
const tints = {
  scans: { bg: colors.mintWash, fg: colors.primary },
  favourites: { bg: '#FFF1F2', fg: '#E11D48' },
  vehicle: { bg: '#EFF6FF', fg: '#2563EB' },
  language: { bg: '#EEF2FF', fg: '#4F46E5' },
  about: { bg: '#F0FDFA', fg: colors.primary },
  terms: { bg: colors.surfaceMuted, fg: '#475569' },
  privacy: { bg: '#ECFDF5', fg: '#059669' },
  feedback: { bg: '#FFFBEB', fg: '#D97706' },
  delete: { bg: '#FFF1F2', fg: colors.error },
  dev: { bg: colors.surfaceMuted, fg: colors.text },
};

// Taps on the version number that unlock Developer settings, each within
// DEV_TAP_WINDOW_MS of the one before.
const DEV_TAPS = 5;
const DEV_TAP_WINDOW_MS = 1500;

// 24px line glyphs, drawn inline so stroke colour follows the theme.
const Glyph = ({ children, color, size = 18, fill = 'none' }) => (
  <Svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke={color}
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round">
    {children}
  </Svg>
);

const ScanGlyph = ({ color }) => (
  <Glyph color={color}>
    <Path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" />
    <Rect x={7} y={7} width={3} height={3} />
    <Rect x={14} y={14} width={3} height={3} />
    <Path d="M7 12h10" />
  </Glyph>
);

const HeartGlyph = ({ color }) => (
  <Glyph color={color} fill={color}>
    <Path d="M12 20s-7-4.4-9.3-8.6C1 8.2 2.9 4.5 6.5 4.5c2.1 0 3.5 1.2 4.3 2.5h2.4c.8-1.3 2.2-2.5 4.3-2.5 3.6 0 5.5 3.7 3.8 6.9C19 15.6 12 20 12 20z" />
  </Glyph>
);

// Car icon from the shared icon set.
const CarGlyph = ({ color }) => <AppIcon name="car" size={18} color={color} />;

const GlobeGlyph = ({ color }) => (
  <Glyph color={color}>
    <Circle cx={12} cy={12} r={9} />
    <Path d="M3 12h18M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
  </Glyph>
);

const DropletGlyph = ({ color }) => (
  <Glyph color={color} fill={color}>
    <Path d="M12 3s6 6.4 6 11a6 6 0 0 1-12 0c0-4.6 6-11 6-11z" />
  </Glyph>
);

const ShieldGlyph = ({ color }) => (
  <Glyph color={color}>
    <Path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <Path d="M12 3v18" />
  </Glyph>
);

const LockGlyph = ({ color }) => (
  <Glyph color={color}>
    <Rect x={5} y={11} width={14} height={10} rx={2} />
    <Path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </Glyph>
);

const MessageGlyph = ({ color }) => (
  <Glyph color={color}>
    <Path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10z" />
    <Path d="M8 8h8M8 12h5" />
  </Glyph>
);

// Wrench, for Developer settings.
const WrenchGlyph = ({ color }) => (
  <Glyph color={color}>
    <Path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.4-.6-.6-2.4 2.5-2.5z" />
  </Glyph>
);

// Shield with a slash, for Delete Account.
const ShieldOffGlyph = ({ color }) => (
  <Glyph color={color}>
    <Path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" />
    <Path d="M9 9l6 6M15 9l-6 6" />
  </Glyph>
);

const PenGlyph = () => (
  <Glyph color={colors.textMuted} size={12}>
    <Path d="M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4" />
  </Glyph>
);

const LogoutGlyph = () => (
  <Glyph color={colors.error} size={18}>
    <Path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
  </Glyph>
);

const ChevronRight = () => (
  <Glyph color={colors.textSubtle} size={16}>
    <Path d="M9 6l6 6-6 6" />
  </Glyph>
);

const initialsOf = name => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
};

// The server's newer `fuelPreference` ('petrol' | 'diesel' | 'lpg' | 'cng' |
// 'ev') in the ids the onboarding / FuelTypeModal use.
const fuelIdFromPreference = preference => {
  const p = String(preference || '').trim().toLowerCase();
  if (p === 'petrol') {
    return 'benzine';
  }
  if (p === 'ev') {
    return 'electric';
  }
  return p || null;
};

// Only the answers that really have a value, so an empty '' on the phone never
// hides a value the server has.
const withoutEmpty = answers =>
  Object.fromEntries(Object.entries(answers || {}).filter(([, v]) => !!v));

// The photo chosen in Edit Profile (kept on this phone) or the initials.
const Avatar = ({ name, photoUri, onPhotoError }) => (
  <View style={styles.avatar}>
    <Svg width={AVATAR_SIZE} height={AVATAR_SIZE} style={StyleSheet.absoluteFill}>
      <Defs>
        <LinearGradient id="avatarFill" x1="0" y1="1" x2="1" y2="0">
          <Stop offset="0" stopColor={colors.primary} />
          <Stop offset="1" stopColor={colors.brandBright} />
        </LinearGradient>
      </Defs>
      <Circle
        cx={AVATAR_SIZE / 2}
        cy={AVATAR_SIZE / 2}
        r={AVATAR_SIZE / 2}
        fill="url(#avatarFill)"
      />
    </Svg>
    {photoUri ? (
      <Image
        key={photoUri}
        source={{ uri: photoUri }}
        style={styles.avatarImage}
        onError={onPhotoError}
      />
    ) : (
      <Text style={styles.avatarText}>{initialsOf(name)}</Text>
    )}
  </View>
);

const SectionLabel = ({ children }) => (
  <Text style={styles.sectionLabel} accessibilityRole="header">
    {children}
  </Text>
);

const SettingsRow = ({ Icon, tint, title, subtitle, trailing, onPress, isLast, dot }) => (
  <>
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}>
      <View style={[styles.iconTile, { backgroundColor: tint.bg }]}>
        <Icon color={tint.fg} />
      </View>
      <View style={styles.rowText}>
        <View style={styles.rowTitleLine}>
          <Text style={styles.rowTitle} numberOfLines={1}>
            {title}
          </Text>
          {dot ? <View style={styles.dot} /> : null}
        </View>
        {subtitle ? (
          <Text style={styles.rowSubtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        ) : null}
      </View>
      {trailing}
      <ChevronRight />
    </Pressable>
    {!isLast && <View style={styles.divider} />}
  </>
);

const CountPill = ({ label, tone = 'neutral' }) => (
  <View style={[styles.pill, tone === 'rose' && styles.pillRose]}>
    <Text style={[styles.pillText, tone === 'rose' && styles.pillTextRose]}>
      {label}
    </Text>
  </View>
);

const ProfileList = ({ navigation }) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [logoutModalVisible, setLogoutModalVisible] = useState(false);
  const [fuelModalVisible, setFuelModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const devSettings = useDevSettings();
  const devTaps = useRef({ count: 0, last: 0 });
  // The page open in the in-app viewer: {title, url} or null
  const [webPage, setWebPage] = useState(null);
  const [favouritesCount, setFavouritesCount] = useState(null);
  // Approved (verified) scans from user/rewards; null until loaded
  const [verifiedScans, setVerifiedScans] = useState(null);
  // The fuel id chosen at onboarding: 'benzine' | 'diesel' | 'electric' | 'lpg'
  const [fuelType, setFuelType] = useState(null);
  // false until the saved answers have been read, so the row can show a
  // placeholder instead of flashing "Not set"
  const [fuelLoaded, setFuelLoaded] = useState(false);
  // answer_1 (frequency) and answer_2 (fuel) ...; the frequency is sent back
  // with a new fuel
  const [savedAnswers, setSavedAnswers] = useState({});
  const [fuelSaving, setFuelSaving] = useState(false);
  const { name, email, phone } = useSelector(state => state.profile);
  const loginName = useSelector(state => state.login.name);
  const loginEmail = useSelector(state => state.login.email);
  const userId = useSelector(state => state.login.userId);

  // When the profile was last downloaded (0 = never)
  const lastProfileFetch = useRef(0);
  // What the last download said, so a refresh skipped as "fresh" still has it
  const serverAnswers = useRef({});
  // Photo chosen in Edit Profile (kept on this phone)
  const [photoUri, setPhotoUri] = useState(null);

  // A different account logged in: forget the previous account's values at
  // once, so they can never show for the new one.
  useEffect(() => {
    lastProfileFetch.current = 0;
    serverAnswers.current = {};
    setPhotoUri(null);
    setFuelType(null);
    setFuelLoaded(false);
    setSavedAnswers({});
    setFavouritesCount(null);
    setVerifiedScans(null);
  }, [userId]);

  // Refresh on focus so counts reflect scans/bookmarks made in other tabs.
  useFocusEffect(
    useCallback(() => {
      let active = true;

      const applyAnswers = answers => {
        if (!active) {
          return;
        }
        const saved = answers?.answer_2 ?? null; // fuel id, e.g. 'benzine'
        setFuelType(saved ? String(saved).toLowerCase() : null);
        setSavedAnswers(answers || {});
        setFuelLoaded(true);
      };

      (async () => {
        // --- diagnostics: which account is the app on, and what is saved ---
        try {
          const userData = await AsyncStorage.getItem('Data');
          console.log(
            '[profile] Data userId:',
            JSON.parse(userData)?.userId,
            '| redux userId:',
            userId,
          );
          const keys = await AsyncStorage.getAllKeys();
          console.log(
            '[profile] saved answer keys:',
            JSON.stringify(keys.filter(k => k.startsWith('onboardingAnswers_'))),
          );
        } catch (e) {}

        // The photo picked in Edit Profile (stored under the same key there)
        try {
          const owner =
            userId || JSON.parse(await AsyncStorage.getItem('Data'))?.userId;
          const uri = await AsyncStorage.getItem(`profilePhoto_${owner}`);
          if (active) {
            setPhotoUri(uri || null);
          }
        } catch (e) {}

        // 1) The copy on this phone is the user's latest choice: show it first.
        //    Without one (new phone, the web app) keep the "···" placeholder
        //    until the server has answered, instead of flashing "Not set".
        const local = withoutEmpty(await getSavedOnboardingAnswers());
        console.log('[profile] local answers:', JSON.stringify(local));
        if (local.answer_2) {
          applyAnswers({ ...serverAnswers.current, ...local });
        }

        // 2) The server only fills gaps. It never replaces what the phone has.
        // Skip the (slow) download when the profile is still fresh.
        if (Date.now() - lastProfileFetch.current < PROFILE_MAX_AGE_MS) {
          console.log('[profile] skipped refresh (data is fresh)');
          if (!local.answer_2) {
            applyAnswers({ ...serverAnswers.current, ...local });
          }
          return;
        }
        const t0 = Date.now();
        try {
          const result = await dispatch(getProfile()).unwrap();
          lastProfileFetch.current = Date.now();
          // {Success, Data: {...}}: the slice may hand back Data or the whole answer
          const payload = result?.Data ?? result;
          console.log('[profile] getProfile ms:', Date.now() - t0);
          // get-profile sends the onboarding answers as `answers`
          // ({fuel_type: 'benzine', fuel_frequency: ...}) and the newer
          // `fuelPreference`; answer_2 is only the old flat name.
          // answers.fuel_type = the onboarding answer, and also what Profile's
          // change (user/save-answers) writes, so it comes first.
          // fuelPreference is only a fallback: the server fills it with the
          // default 'petrol' for every user, even one who chose diesel.
          const serverFuel =
            payload?.answers?.fuel_type ||
            fuelIdFromPreference(payload?.fuelPreference) ||
            payload?.answer_2 ||
            null;
          // Both raw server values, to see which one says what
          console.log(
            '[profile] server answers.fuel_type:',
            payload?.answers?.fuel_type,
            '| fuelPreference:',
            payload?.fuelPreference,
          );
          console.log('[profile] server fuel:', serverFuel);
          const fromServer = {};
          [1, 2, 3, 4, 5].forEach(n => {
            if (payload?.[`answer_${n}`]) {
              fromServer[`answer_${n}`] = payload[`answer_${n}`];
            }
          });
          // The fuel and how often the user refuels come from `answers`.
          // updateFuelType sends the frequency back with the new fuel, so a
          // phone / the web app without a saved copy must have it too.
          if (serverFuel) {
            fromServer.answer_2 = serverFuel;
          }
          if (!fromServer.answer_1 && payload?.answers?.fuel_frequency) {
            fromServer.answer_1 = payload.answers.fuel_frequency;
          }
          serverAnswers.current = fromServer;
          console.log(
            '[profile] fuel source:',
            local.answer_2 ? 'phone' : serverFuel ? 'server' : 'none',
          );
          applyAnswers({ ...fromServer, ...local });
        } catch (e) {
          // the profile call failed: keep what the phone has
          if (active) {
            setFuelLoaded(true);
          }
        }
      })();

      // get-profile's scans_made also counts old uploads that never became
      // a reviewed scan; the pill shows the scans that were approved.
      getRewards()
        .then(rewards => {
          if (active) {
            setVerifiedScans(rewards.scans.approved);
          }
        })
        .catch(() => {});

      const f0 = Date.now();
      getFavouritesApi()
        .then(res => {
          console.log('[profile] favourites ms:', Date.now() - f0);
          if (active && res?.Success) {
            setFavouritesCount(res?.Data?.bookmarks?.length ?? 0);
          }
        })
        .catch(() => { });
      return () => {
        active = false;
      };
    }, [dispatch, userId]),
  );

  const displayName = name || loginName || '';
  const displayEmail = email || loginEmail || '';
  const isEnglish = i18n.language === 'en';

  // A fuel id the app doesn't know (e.g. a new one from the API) is still
  // shown, as plain text, instead of "Not set".
  const knownFuel = FUEL_TYPES.find(f => f.id === fuelType);
  const currentFuel = fuelType
    ? knownFuel || { id: fuelType, title: fuelType, unknown: true }
    : null;

  // Save the new fuel type. Throws on failure so the sheet stays open.
  const handleSaveFuel = async newFuel => {
    setFuelSaving(true);
    try {
      // Same endpoint onboarding used (user/save-answers); the refuel
      // frequency is sent along so it is never lost.
      await updateFuelType(newFuel, savedAnswers);
      setFuelType(newFuel);
      setSavedAnswers(prev => ({ ...prev, answer_2: newFuel }));
      Toast.show({
        type: 'success',
        text1: t('root.Toast.Success.ProfileUpdatedSuccess'),
      });
    } catch (err) {
      Toast.show({
        type: 'error',
        text1:
          err?.message || t('root.Toast.Error.FailedToUpdateProfile'),
      });
      throw err;
    } finally {
      setFuelSaving(false);
    }
  };

  const handleVersionTap = async () => {
    const now = Date.now();
    const taps = devTaps.current;
    taps.count = now - taps.last < DEV_TAP_WINDOW_MS ? taps.count + 1 : 1;
    taps.last = now;
    if (taps.count < DEV_TAPS) {
      return;
    }
    taps.count = 0;
    if (!devSettings?.unlocked) {
      await setDevUnlocked(true);
      Toast.show({ type: 'success', text1: t('root.DevSettings.unlocked') });
    }
    navigation.navigate('DevSettings');
  };

  // Delete the account on the server, and only if that succeeds log the user
  // out. If it fails the user stays logged in and sees the error.
  const handleDeleteAccount = async () => {
    if (deleting) {
      return;
    }
    setDeleting(true);
    try {
      await deleteAccount();
    } catch (err) {
      setDeleting(false);
      Toast.show({
        type: 'error',
        text1:
          err?.message ||
          t('root.DeleteAccount.failed', 'Could not delete your account. Please try again.'),
      });
      return;
    }
    setDeleting(false);
    setDeleteModalVisible(false);
    Toast.show({
      type: 'success',
      text1: t('root.DeleteAccount.success', 'Your account has been deleted'),
    });
    dispatch(logoutUser());
  };

  return (
    <View style={styles.screen}>
      <LanguageModal
        modalVisible={modalVisible}
        setModalVisible={setModalVisible}
      />
      <FuelTypeModal
        visible={fuelModalVisible}
        onClose={() => setFuelModalVisible(false)}
        value={fuelType}
        onSave={handleSaveFuel}
        loading={fuelSaving}
      />
      <LogoutConfirm
        modalVisible={logoutModalVisible}
        setModalVisible={setLogoutModalVisible}
        onConfirm={() => {
          dispatch(logoutUser());
        }}
      />
      <DeleteAccountModal
        visible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onConfirm={handleDeleteAccount}
        loading={deleting}
      />
      <InAppWebView
        visible={!!webPage}
        title={webPage?.title}
        url={webPage?.url}
        onClose={() => setWebPage(null)}
      />
      <AuthHeader title={t('root.ProfileList.accountTitle')} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        showsVerticalScrollIndicator={false}>
        <View style={[styles.card, styles.profileCard]}>
          <Avatar
            name={displayName}
            photoUri={photoUri}
            onPhotoError={() => setPhotoUri(null)}
          />
          <View style={styles.profileText}>
            <Text style={styles.name} numberOfLines={1}>
              {displayName}
            </Text>
            {displayEmail ? (
              <Text style={styles.email} numberOfLines={1}>
                {displayEmail}
              </Text>
            ) : null}
            {phone ? (
              <Text style={styles.phone} numberOfLines={1}>
                {phone}
              </Text>
            ) : null}
            <Pressable
              onPress={() => navigation.navigate('Profile')}
              accessibilityRole="button"
              style={({ pressed }) => [
                styles.editButton,
                pressed && styles.editButtonPressed,
              ]}>
              <PenGlyph />
              <Text style={styles.editButtonText}>
                {t('root.ProfileList.editProfile')}
              </Text>
            </Pressable>
          </View>
        </View>

        <View style={styles.section}>
          <SectionLabel>{t('root.ProfileList.sectionActivity')}</SectionLabel>
          <View style={styles.card}>
            <View style={styles.cardClip}>
              <SettingsRow
                Icon={ScanGlyph}
                tint={tints.scans}
                title={t('root.ProfileList.myScans')}
                subtitle={t('root.ProfileList.myScansSubtitle')}
                trailing={
                  verifiedScans != null ? (
                    <CountPill
                      label={t('root.ProfileList.verifiedCount', {
                        count: verifiedScans,
                      })}
                    />
                  ) : null
                }
                onPress={() =>
                  navigation.navigate('ScansTab', {
                    screen: 'Scans',
                    params: { from: 'ProfileList' },
                  })
                }
              />
              <SettingsRow
                Icon={HeartGlyph}
                tint={tints.favourites}
                title={t('root.ProfileList.Favourites')}
                subtitle={t('root.ProfileList.favouritesSubtitle')}
                trailing={
                  favouritesCount != null ? (
                    <CountPill
                      tone="rose"
                      label={t('root.ProfileList.savedCount', {
                        count: favouritesCount,
                      })}
                    />
                  ) : (
                    <CountPill tone="rose" label="–" />
                  )
                }
                onPress={() => navigation.navigate('Favourites')}
                isLast
              />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <SectionLabel>{t('root.ProfileList.sectionSettings')}</SectionLabel>
          <View style={styles.card}>
            <View style={styles.cardClip}>
              <SettingsRow
                Icon={CarGlyph}
                tint={tints.vehicle}
                title={t('root.ProfileList.vehicleFuel', 'Vehicle & Fuel Type')}
                subtitle={t(
                  'root.ProfileList.vehicleFuelSubtitle',
                  'Primary preferences',
                )}
                trailing={
                  <View style={styles.languageChip}>
                    {fuelLoaded && currentFuel && !currentFuel.unknown ? (
                      <AppIcon
                        name={currentFuel.id}
                        size={14}
                        color={colors.primary}
                      />
                    ) : null}
                    <Text style={styles.languageChipText}>
                      {!fuelLoaded
                        ? '···'
                        : currentFuel
                          ? t(
                            `root.ProfileList.fuel.${currentFuel.id}`,
                            currentFuel.title,
                          )
                          : t('root.ProfileList.fuelNotSet', 'Not set')}
                    </Text>
                  </View>
                }
                onPress={() => setFuelModalVisible(true)}
              />
              <SettingsRow
                Icon={GlobeGlyph}
                tint={tints.language}
                title={t('root.ProfileList.changeLanguage')}
                subtitle={t('root.ProfileList.languageSubtitle')}
                trailing={
                  <View style={styles.languageChip}>
                    {isEnglish ? (
                      <EnglishFlag width={16} height={16} />
                    ) : (
                      <DutchFlag width={16} height={16} />
                    )}
                    <Text style={styles.languageChipText}>
                      {isEnglish ? 'English' : 'Nederlands'}
                    </Text>
                  </View>
                }
                onPress={() => setModalVisible(true)}
                isLast
              />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <SectionLabel>{t('root.ProfileList.sectionAbout')}</SectionLabel>
          <View style={styles.card}>
            <View style={styles.cardClip}>
              <SettingsRow
                Icon={DropletGlyph}
                tint={tints.about}
                title={t('root.ProfileList.aboutPompi')}
                subtitle={t('root.ProfileList.aboutSubtitle')}
                dot
                onPress={() =>
                  setWebPage({
                    title: t('root.ProfileList.aboutPompi'),
                    url: WEB_PAGES.about,
                  })
                }
              />
              <SettingsRow
                Icon={ShieldGlyph}
                tint={tints.terms}
                title={t('root.ProfileList.TermsAndConditions')}
                subtitle={t('root.ProfileList.termsSubtitle')}
                onPress={() =>
                  setWebPage({
                    title: t('root.ProfileList.TermsAndConditions'),
                    url: WEB_PAGES.terms,
                  })
                }
              />
              <SettingsRow
                Icon={LockGlyph}
                tint={tints.privacy}
                title={t('root.ProfileList.privacyPolicy', 'Privacy Policy')}
                subtitle={t(
                  'root.ProfileList.privacySubtitle',
                  'Data protection & privacy rights',
                )}
                onPress={() =>
                  setWebPage({
                    title: t('root.ProfileList.privacyPolicy', 'Privacy Policy'),
                    url: WEB_PAGES.privacy,
                  })
                }
                isLast
              />
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <SectionLabel>{t('root.ProfileList.sectionSupport')}</SectionLabel>
          <View style={styles.card}>
            <View style={styles.cardClip}>
              <SettingsRow
                Icon={MessageGlyph}
                tint={tints.feedback}
                title={t('root.ProfileList.sendFeedback')}
                subtitle={t('root.ProfileList.sendFeedbackSubtitle')}
                onPress={() => navigation.navigate('Feedback')}
              />
              <SettingsRow
                Icon={MessageGlyph}
                tint={tints.feedback}
                title={t('root.ProfileList.myFeedback')}
                subtitle={t('root.ProfileList.myFeedbackSubtitle')}
                onPress={() => navigation.navigate('MyFeedback')}
                isLast
              />
            </View>
          </View>
        </View>

        {devSettings?.unlocked ? (
          <View style={styles.section}>
            <View style={styles.card}>
              <View style={styles.cardClip}>
                <SettingsRow
                  Icon={WrenchGlyph}
                  tint={tints.dev}
                  title={t('root.DevSettings.title')}
                  subtitle={t('root.DevSettings.subtitle')}
                  onPress={() => navigation.navigate('DevSettings')}
                  isLast
                />
              </View>
            </View>
          </View>
        ) : null}

        <Pressable
          onPress={() => setLogoutModalVisible(true)}
          accessibilityRole="button"
          style={({ pressed }) => [
            styles.logoutButton,
            pressed && styles.logoutButtonPressed,
          ]}>
          <LogoutGlyph />
          <Text style={styles.logoutText}>{t('root.ProfileList.Logout')}</Text>
        </Pressable>

        <Pressable
          onPress={() => setDeleteModalVisible(true)}
          accessibilityRole="button"
          style={({ pressed }) => [
            styles.deleteButton,
            pressed && styles.deleteButtonPressed,
          ]}>
          <View style={[styles.iconTile, { backgroundColor: tints.delete.bg }]}>
            <ShieldOffGlyph color={tints.delete.fg} />
          </View>
          <View style={styles.rowText}>
            <Text style={styles.deleteText} numberOfLines={1}>
              {t('root.ProfileList.deleteAccount')}
            </Text>
            <Text style={styles.rowSubtitle} numberOfLines={1}>
              {t('root.ProfileList.deleteAccountSubtitle')}
            </Text>
          </View>
          <ChevronRight />
        </Pressable>

        <Pressable onPress={handleVersionTap} hitSlop={12}>
          <Text style={styles.versionText}>
            {t('root.ProfileList.version', { version: APP_VERSION })}
          </Text>
        </Pressable>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.margin,
    paddingBottom: spacing.xxl,
    gap: spacing.margin,
  },
  card: {
    ...card,
  },
  // Inner clip so pressed rows respect the radius without clipping the
  // card's shadow on iOS.
  cardClip: {
    borderRadius: radii.card - 1,
    overflow: 'hidden',
  },

  // Profile summary
  profileCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.margin,
    gap: 14,
  },
  avatar: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarImage: {
    width: AVATAR_SIZE,
    height: AVATAR_SIZE,
    borderRadius: AVATAR_SIZE / 2,
  },
  avatarText: {
    fontFamily: fonts.bold,
    fontSize: 20,
    letterSpacing: 1,
    color: colors.onPrimary,
  },
  profileText: {
    flex: 1,
    minWidth: 0,
    alignItems: 'flex-start',
  },
  name: {
    fontFamily: fonts.bold,
    fontSize: 16,
    lineHeight: 22,
    color: colors.textStrong,
    alignSelf: 'stretch',
  },
  email: {
    ...typography.bodySm,
    color: colors.textMuted,
    marginTop: 2,
    alignSelf: 'stretch',
  },
  phone: {
    fontFamily: fonts.regular,
    fontSize: 11,
    lineHeight: 14,
    color: colors.textSubtle,
    marginTop: 2,
    alignSelf: 'stretch',
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
    paddingHorizontal: spacing.md,
    paddingVertical: 5,
    borderRadius: radii.pill,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background,
  },
  editButtonPressed: {
    backgroundColor: colors.surfaceMuted,
    transform: [{ scale: 0.97 }],
  },
  editButtonText: {
    ...typography.labelMd,
    letterSpacing: 0,
    color: colors.textEmphasis,
  },

  // Sections
  section: {
    gap: 6,
  },
  sectionLabel: {
    ...typography.labelMd,
    fontFamily: fonts.bold,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: colors.textSubtle,
    paddingHorizontal: spacing.xs,
  },

  // Rows
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 64,
    paddingHorizontal: spacing.margin,
    paddingVertical: 14,
    gap: spacing.md,
    backgroundColor: colors.surface,
  },
  rowPressed: {
    backgroundColor: colors.surfaceMuted,
  },
  iconTile: {
    width: ICON_TILE,
    height: ICON_TILE,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowText: {
    flex: 1,
    minWidth: 0,
  },
  rowTitleLine: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  rowTitle: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.textStrong,
    flexShrink: 1,
  },
  rowSubtitle: {
    fontFamily: fonts.regular,
    fontSize: 11,
    lineHeight: 15,
    color: colors.textSubtle,
    marginTop: 1,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: colors.secondary,
  },
  divider: {
    height: 1,
    backgroundColor: colors.borderSubtle,
  },

  // Trailing accessories
  pill: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
  },
  pillRose: {
    backgroundColor: tints.favourites.bg,
  },
  pillText: {
    ...typography.labelMd,
    letterSpacing: 0,
    color: '#475569',
  },
  pillTextRose: {
    color: tints.favourites.fg,
  },
  languageChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 6,
    backgroundColor: colors.surfaceMuted,
  },
  languageChipText: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textEmphasis,
  },

  // Footer
  logoutButton: {
    ...card,
    borderColor: colors.errorContainer,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    minHeight: 52,
    marginTop: spacing.xs,
  },
  logoutButtonPressed: {
    backgroundColor: colors.errorTint,
    transform: [{ scale: 0.99 }],
  },
  logoutText: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.error,
  },
  // Row-shaped card, like the settings rows but on its own.
  deleteButton: {
    ...card,
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 64,
    paddingHorizontal: spacing.margin,
    paddingVertical: 14,
    gap: spacing.md,
  },
  deleteButtonPressed: {
    backgroundColor: colors.errorTint,
    transform: [{ scale: 0.99 }],
  },
  deleteText: {
    ...typography.labelLg,
    letterSpacing: 0,
    color: colors.error,
  },
  versionText: {
    ...typography.labelMd,
    color: colors.textSubtle,
    textAlign: 'center',
  },
});

export default ProfileList;